import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useToastContext } from '@/hooks/useToastContext';
import styles from './LoginPage.module.css';

/**
 * Страница входа
 */
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { login } = useAuth();
  const { showError } = useToastContext();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(email, password);
      // Навигация происходит в AuthContext
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Произошла ошибка при входе';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [email, password, login, showError]);

  const isFormValid = email.trim() !== '' && password.trim() !== '';

  return (
    <div className={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.content}
      >
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className={styles.title}
        >
          Вход в админ-панель
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={styles.subtitle}
        >
          Войдите в систему для доступа к админ-панели
        </motion.p>

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          onSubmit={handleSubmit}
          className={styles.form}
        >
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={styles.input}
                placeholder="Введите email"
                disabled={loading}
                autoComplete="email"
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Пароль
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={styles.input}
                placeholder="Введите пароль"
                disabled={loading}
                autoComplete="current-password"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || loading}
            className={styles.submitButton}
          >
            {loading ? 'Вход...' : 'Войти'}
          </button>

          <div className={styles.linkContainer}>
            <span>Нет аккаунта?</span>
            <Link to="/register" className={styles.link}>
              Зарегистрироваться
            </Link>
          </div>

          <div className={styles.linkContainer}>
            <Link to="/" className={styles.backLink}>
              ← Вернуться на главную
            </Link>
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
};

export default LoginPage;

